import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Search, Pencil, Trash2, UserCheck, Key, Download } from 'lucide-react';
import { toast } from 'sonner';
import SetPasswordDialog from '../../components/SetPasswordDialog';

const defaultForm = { first_name: '', last_name: '', email: '', phone: '', relationship: 'Parent', student_ids: [], address: '', occupation: '' };

export default function ParentManagement() {
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  const load = () => {
    const params = search ? `?search=${search}` : '';
    api.get(`/admin/parents${params}`).then(r => setParents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [search]);
  useEffect(() => { api.get('/admin/students').then(r => setStudents(r.data)).catch(() => {}); }, []);

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/admin/parents/${editing}`, form);
        toast.success('Parent updated');
      } else {
        await api.post('/admin/parents', form);
        toast.success('Parent created');
      }
      setShowDialog(false); setForm(defaultForm); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleEdit = (p) => {
    setForm({ first_name: p.first_name, last_name: p.last_name, email: p.email, phone: p.phone || '', relationship: p.relationship || 'Parent', student_ids: p.student_ids || [], address: p.address || '', occupation: p.occupation || '' });
    setEditing(p.id); setShowDialog(true);
  };

  const toggleStudent = (sid) => {
    setForm(f => ({
      ...f,
      student_ids: f.student_ids.includes(sid) ? f.student_ids.filter(id => id !== sid) : [...f.student_ids, sid]
    }));
  };

  return (
    <div data-testid="parent-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><UserCheck className="w-6 h-6" /> Parents / Guardians</h1>
          <p className="text-sm text-slate-500">{parents.length} active parents</p>
        </div>
        <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }} className="bg-[#1B2A4A] hover:bg-[#283E6B]" data-testid="add-parent-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Parent
        </Button>
        <Button variant="outline" className="border-[#1B2A4A] text-[#1B2A4A]" onClick={() => { const token = localStorage.getItem('token'); fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/parents/export`, { headers: { Authorization: `Bearer ${token}` } }).then(r=>r.blob()).then(b=>{ const a=document.createElement('a'); a.href=URL.createObjectURL(b); a.download='parents_export.csv'; a.click(); }); }} data-testid="export-parents-csv">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>
      <div className="relative mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search parents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 max-w-md" data-testid="search-parents-input" />
      </div>
      <div className="bg-white rounded-xl border shadow-sm">
        <Table>
          <TableHeader><TableRow className="data-table">
            <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Relationship</TableHead><TableHead>Linked Children</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {parents.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No parents found</TableCell></TableRow>
            ) : parents.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-semibold text-[#1B2A4A]">{p.first_name} {p.last_name}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.email}</TableCell>
                <TableCell>{p.phone}</TableCell>
                <TableCell>{p.relationship}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(p.student_ids||[]).map(sid => {
                      const s = students.find(st => st.id === sid);
                      return <Badge key={sid} variant="outline" className="text-xs">{s ? `${s.first_name} ${s.last_name}` : sid}</Badge>;
                    })}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Set Password" onClick={() => setPwUser({ id: p.user_id, name: `${p.first_name} ${p.last_name}`, email: p.email })} data-testid={`set-password-parent-${p.id}`}><Key className="w-4 h-4 text-amber-600" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={async () => { if(window.confirm('Deactivate?')){ await api.delete(`/admin/parents/${p.id}`); toast.success('Deactivated'); load(); }}}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Parent' : 'Add Parent'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} data-testid="parent-first-name" /></div>
            <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} data-testid="parent-last-name" /></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="parent-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Relationship</Label><Input value={form.relationship} onChange={e => setForm({...form, relationship: e.target.value})} /></div>
            <div><Label>Occupation</Label><Input value={form.occupation} onChange={e => setForm({...form, occupation: e.target.value})} /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
            <div className="col-span-2">
              <Label className="mb-2 block">Link Children</Label>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {students.length === 0 ? <p className="text-xs text-slate-400 p-2">No students. Create students first.</p> :
                  students.map(s => (
                    <label key={s.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50 ${form.student_ids.includes(s.id) ? 'bg-blue-50' : ''}`}>
                      <input type="checkbox" checked={form.student_ids.includes(s.id)} onChange={() => toggleStudent(s.id)} className="rounded" />
                      <span className="text-sm">{s.first_name} {s.last_name} <span className="text-xs text-slate-400">({s.grade})</span></span>
                    </label>
                  ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#1B2A4A]" data-testid="save-parent-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      {pwUser && <SetPasswordDialog open={!!pwUser} onOpenChange={() => setPwUser(null)} userId={pwUser.id} userName={pwUser.name} userEmail={pwUser.email} />}
    </div>
  );
}
